import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import type {
  BackendUpdateResponse,
  DatastoreDocument,
  RequestStatus,
} from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  EquipmentGroup,
  EquipmentGroupCollection,
  UsersSelfCollection,
} from '@freelancer/datastore/collections';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { EquipmentApi } from 'api-typings/common/common';
import type { SelectorItem } from 'app/item-selector/item-selector.types';
import { SelectorButtonType } from 'app/item-selector/item-selector.types';
import { EquipmentGroupName } from 'app/jobs/job-post/freightlancer/form/freightlancer-form.model';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, firstValueFrom } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';

@UntilDestroy({ className: 'VehicleTypesFormComponent' })
@Component({
  selector: 'app-vehicle-types-form',
  template: `
    <fl-banner-alert
      *ngIf="(updateUserVehicleTypesPromise | flAsync)?.status === 'error'"
      i18n="Vehicle types error"
      [flMarginBottom]="'small'"
      [type]="BannerAlertType.ERROR"
    >
      <ng-container
        *ngIf="(updateUserVehicleTypesPromise | flAsync)?.errorCode"
      >
        An error occurred while updating your truck types.
      </ng-container>
    </fl-banner-alert>

    <app-item-selector
      i18n-itemCardHeading="Heading for the card that contains the items"
      i18n-itemName="Name of the items in the selector"
      i18n-formHeading="Item selector form heading"
      i18n-noSelectedHeading="Heading to show if there is no selected item"
      i18n-noSelectedMessage="Message to show if there is no selected item"
      itemCardHeading="Truck Specifications"
      itemName="Truck type"
      formHeading="Select your truck types"
      noSelectedHeading="No truck types selected"
      noSelectedMessage="Please select your truck types so we can notify you on suitable jobs. Multiple selections allowed."
      [buttonType]="buttonType"
      [itemsList]="vehicleTypesListItems$ | flAsync"
      [isBusySaving]="
        updateUserVehicleTypesPromise &&
        (updateUserVehicleTypesPromise | flAsync) === undefined
      "
      [footerStickyStatic]="footerStickyStatic"
      [footerFullWidth]="footerFullWidth"
      [pageStatus]="pageStatus$ | flAsync"
      [selectedItems]="selectedVehicleTypeItems$ | flAsync"
      [showBackButton]="showBackButton"
      (onDeselectItem)="handleDeselectVehicleType($event)"
      (onSelectItem)="handleSelectVehicleType($event)"
      (onBackButtonPressed)="handleBack()"
      (onSaveItems)="saveVehicleTypeChanges()"
    >
    </app-item-selector>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleTypesFormComponent implements OnInit {
  BannerAlertType = BannerAlertType;

  @Input() buttonType: SelectorButtonType = SelectorButtonType.SAVE;
  @Input() footerStickyStatic = false;
  @Input() footerFullWidth = true;
  @Input() showBackButton = false;

  // Pre-selected vehicle type ids
  @Input() preSelectedVehicleTypeIds?: readonly number[];

  @Output() onSaveVehicleTypes = new EventEmitter<readonly EquipmentApi[]>();
  @Output() onBackButtonPressed = new EventEmitter<void>();

  private selectedVehicleTypesSubject$: BehaviorSubject<
    readonly EquipmentApi[]
  >;

  vehicleTypesList$: Observable<readonly EquipmentApi[]>;
  vehicleTypesListItems$: Observable<readonly SelectorItem[]>;
  pageStatus$: Observable<RequestStatus<any>>;
  selectedVehicleTypes$: Observable<readonly EquipmentApi[]>;
  selectedVehicleTypeItems$: Observable<readonly SelectorItem[]>;
  vehicleTypeGroupId: number;
  userSelfDocument: DatastoreDocument<UsersSelfCollection>;

  updateUserVehicleTypesPromise?: Promise<
    BackendUpdateResponse<UsersSelfCollection> | undefined
  >;

  constructor(
    private auth: Auth,
    private datastore: Datastore,
  ) {}

  ngOnInit(): void {
    const vehicleTypesListCollection =
      this.datastore.collection<EquipmentGroupCollection>('equipmentGroup');

    this.vehicleTypesList$ = vehicleTypesListCollection.valueChanges().pipe(
      map(equipmentGroups => {
        // FIXME: T274808 There's no guarantee that this is defined
        const vehicleTypeGroup = equipmentGroups.find(
          equipmentGroup =>
            equipmentGroup.name === EquipmentGroupName.VEHICLE_TYPE,
        ) as EquipmentGroup;
        this.vehicleTypeGroupId = vehicleTypeGroup.id;
        return vehicleTypeGroup.items ?? [];
      }),
    );

    this.selectedVehicleTypesSubject$ = new BehaviorSubject<
      readonly EquipmentApi[]
    >([]);
    this.selectedVehicleTypes$ =
      this.selectedVehicleTypesSubject$.asObservable();

    this.selectedVehicleTypeItems$ = this.selectedVehicleTypes$.pipe(
      map(selectedVehicleTypes => {
        return selectedVehicleTypes.map(vehicleType => {
          return {
            id: vehicleType.id,
            name: vehicleType.name || '',
            isSelected: true,
          };
        });
      }),
    );

    this.userSelfDocument = this.datastore.document<UsersSelfCollection>(
      'usersSelf',
      this.auth.getUserId(),
    );
    firstValueFrom(
      this.userSelfDocument.valueChanges().pipe(untilDestroyed(this)),
    ).then(userSelf => {
      if (userSelf.equipmentGroups) {
        const vehicleTypeGroup = userSelf.equipmentGroups.find(
          group => group.name === EquipmentGroupName.VEHICLE_TYPE,
        );
        if (vehicleTypeGroup) {
          this.selectedVehicleTypesSubject$.next(vehicleTypeGroup.items ?? []);
        }
      }
    });

    this.vehicleTypesListItems$ = combineLatest([
      this.vehicleTypesList$,
      this.selectedVehicleTypes$,
    ]).pipe(
      map(([vehicleTypesList, selectedVehicleTypes]) => {
        return vehicleTypesList?.map(vehicleType => ({
          id: vehicleType.id,
          name: vehicleType.name || '',
          isSelected:
            selectedVehicleTypes.filter(
              selectedVehicleType => selectedVehicleType.id === vehicleType.id,
            ).length > 0,
        }));
      }),
    );

    this.pageStatus$ = combineLatest([
      vehicleTypesListCollection.status$,
      this.userSelfDocument.status$,
    ]).pipe(
      map(
        (statuses: readonly RequestStatus<any>[]) =>
          statuses.find(status => !status.ready || status.error) || {
            ready: true,
          },
      ),
    );

    if (this.preSelectedVehicleTypeIds) {
      firstValueFrom(this.vehicleTypesList$.pipe(untilDestroyed(this))).then(
        vehicleTypesList => {
          const selectedVehicleTypes: EquipmentApi[] = [];

          vehicleTypesList.forEach(vehicleType => {
            const includeVehicleType = this.preSelectedVehicleTypeIds?.includes(
              vehicleType.id,
            );
            if (includeVehicleType) {
              selectedVehicleTypes.push(vehicleType);
            }
          });

          this.selectedVehicleTypesSubject$.next(selectedVehicleTypes);
        },
      );
    }
  }

  handleSelectVehicleType(selectedItem: SelectorItem): void {
    firstValueFrom(
      this.selectedVehicleTypes$.pipe(
        withLatestFrom(this.vehicleTypesList$),
        untilDestroyed(this),
      ),
    ).then(([selectedVehicleTypes, vehicleTypesList]) => {
      this.selectedVehicleTypesSubject$.next([
        ...selectedVehicleTypes,
        // FIXME: T274808 There's no guarantee that this is defined
        vehicleTypesList.find(
          vehicleType => vehicleType.id === selectedItem.id,
        ) as EquipmentApi,
      ]);
    });
  }

  handleDeselectVehicleType(deselectedItem: SelectorItem): void {
    firstValueFrom(this.selectedVehicleTypes$.pipe(untilDestroyed(this))).then(
      vehicleTypes => {
        this.selectedVehicleTypesSubject$.next(
          vehicleTypes.filter(
            vehicleType => vehicleType.id !== deselectedItem.id,
          ),
        );
      },
    );
  }

  async saveVehicleTypeChanges(): Promise<void> {
    const selectedVehicleTypes = await firstValueFrom(
      this.selectedVehicleTypes$.pipe(untilDestroyed(this)),
    );

    const selectedItems = {
      id: this.vehicleTypeGroupId,
      items: selectedVehicleTypes,
    };
    this.updateUserVehicleTypesPromise = this.userSelfDocument.update({
      equipmentGroups: [selectedItems],
    });

    const result = await this.updateUserVehicleTypesPromise;

    if (result?.status === 'success') {
      this.onSaveVehicleTypes.emit(selectedVehicleTypes);
    }
  }

  handleBack(): void {
    this.onBackButtonPressed.emit();
  }
}
